import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoTubePlateJSON {
  type: GeckoFeatureModelType.TubePlate
  name: string
  feature_geometry: {
    tube_start: number
    tube_end: number
    y_start: number
    y_end: number
  }
  surface_specs: {
    wall_nominal_thickness: number | undefined
    coating_nominal_thickness: number | undefined
  }
  installation_date: string | undefined
}

export const NewGeckoTubePlateJSON: GeckoTubePlateJSON = {
  type: GeckoFeatureModelType.TubePlate,
  name: 'Plate',
  feature_geometry: {
    tube_start: 0,
    tube_end: 0,
    y_start: 0,
    y_end: 0,
  },
  surface_specs: {
    wall_nominal_thickness: undefined,
    coating_nominal_thickness: undefined,
  },
  installation_date: undefined,
}

export class GeckoTubePlate extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.TubePlate
  readonly geometry: GeckoComponentRect
  readonly wallNominalThickness: GeckoMeasurement | undefined
  readonly coatingNominalThickness: GeckoMeasurement | undefined
  readonly installationDate: Date | undefined

  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    installationDate: Date | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.installationDate = installationDate
  }

  public get isXWrapped() {
    return this.geometry.x.start.num >= this.geometry.x.end.num
  }

  public get isYWrapped() {
    return this.geometry.y.start.num >= this.geometry.y.end.num
  }

  public static CreateFromJSON(
    componentID: string,
    plateID: string,
    plateJSON: GeckoTubePlateJSON
  ): GeckoTubePlate {
    const thicknessMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometryData = plateJSON.feature_geometry as Record<
      string,
      number
    >
    const plateGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      featureGeometryData.tube_start as number,
      featureGeometryData.y_start as number,
      featureGeometryData.tube_end as number,
      featureGeometryData.y_end as number,
      GeckoMeasurementUnit.Tubes,
      GeckoMeasurementUnit.Meters
    )

    const surfaceSpecs = plateJSON.surface_specs as Record<string, number>

    const wallNominalThickness =
      surfaceSpecs.wall_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.wall_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined
    const coatingNominalThickness =
      surfaceSpecs.coating_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.coating_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined

    const installationDate =
      plateJSON.installation_date !== undefined
        ? parseDate(plateJSON.installation_date)
        : undefined
    const plateName = plateJSON.name as string

    return new GeckoTubePlate(
      plateID,
      plateName,
      plateGeometry,
      wallNominalThickness,
      coatingNominalThickness,
      installationDate
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoTubePlateJSON>
  ): Map<string, GeckoTubePlate> {
    return Object.keys(recordsJSON).reduce((resultObj, plateID) => {
      resultObj.set(
        plateID,
        GeckoTubePlate.CreateFromJSON(
          componentID,
          plateID,
          recordsJSON[plateID]
        )
      )
      return resultObj
    }, new Map<string, GeckoTubePlate>())
  }

  asJSON() {
    return generateTubePlate(
      this.name,
      this.geometry,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.installationDate
    )
  }
}

export function generateTubePlate(
  name: string,
  geometry: GeckoComponentRect,
  wallNominalThickness?: GeckoMeasurement,
  coatingNominalThickness?: GeckoMeasurement,
  installationDate?: Date
): GeckoTubePlateJSON {
  const geometryInTubesMeters = geometry.convertTo(
    GeckoMeasurementUnit.Tubes,
    GeckoMeasurementUnit.Meters
  )
  const plateJSON: GeckoTubePlateJSON = {
    type: GeckoFeatureModelType.TubePlate,
    name: name,
    installation_date: installationDate
      ? installationDate.toISOString().split('T')[0]
      : undefined,
    feature_geometry: {
      tube_start: geometryInTubesMeters.x.start.num,
      y_start: geometryInTubesMeters.y.start.num,
      tube_end: geometryInTubesMeters.x.end.num,
      y_end: geometryInTubesMeters.y.end.num,
    },
    surface_specs: {
      wall_nominal_thickness:
        wallNominalThickness !== undefined
          ? wallNominalThickness.num
          : undefined,
      coating_nominal_thickness:
        coatingNominalThickness !== undefined
          ? coatingNominalThickness.num
          : undefined,
    },
  }

  return plateJSON
}

export const TubePlateFormFields = (plateJSON: GeckoTubePlateJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Plate Name',
      value: plateJSON.name,
      unit_magnitude: null,
      initialValue: 'Plate',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(plateJSON.installation_date, false),
    {
      dataKey: 'wall_nominal_thickness',
      label: 'Wall Nominal Thickness',
      value: plateJSON.surface_specs.wall_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'coating_nominal_thickness',
      label: 'Coating Nominal Thickness',
      value: plateJSON.surface_specs.coating_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_start',
      label: 'Tube Start',
      value: plateJSON.feature_geometry.tube_start,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_end',
      label: 'Tube End',
      value: plateJSON.feature_geometry.tube_end,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: plateJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: plateJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const TubePlateJSONFromFormFields = (formFields: FormField[]) => {
  const wallNominalThickness = getFormFieldByDataKey(
    formFields,
    'wall_nominal_thickness'
  )
  const coatingNominalThickness = getFormFieldByDataKey(
    formFields,
    'coating_nominal_thickness'
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generateTubePlate(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoComponentRect.CreateFromXYs(
      'fake_component_id',
      getFormFieldByDataKey(formFields, 'tube_start')?.value as number,
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      getFormFieldByDataKey(formFields, 'tube_end')?.value as number,
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    ),
    wallNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined
  )
}

export const TubePlateFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.TubePlate,
  title: 'Plates',
  singularName: 'Plate',
  fields: ['name', 'tube_start', 'tube_end', 'y_start', 'y_end'],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
}
